<script setup lang="ts">
import { storeToRefs } from 'pinia';

import FoxAndClover from '@clovyr/bed/assets/images/fox-and-clover.png';
import { formatWNextPrice, usePrice } from '@clovyr/bed/composables/usePrice';

import AppLauncherHero from '@/components/tiers/AppLauncherHero.vue';
import { useLauncherStore } from '@/stores/launcher_store';

const launcherStore = useLauncherStore();
const { metadata, manifest, deployment, billingPeriod, salesTier, salesTierLabel } =
  storeToRefs(launcherStore);
const { setBillingPeriod } = launcherStore;

const computeSize = computed(
  () => deployment.value.size || manifest.value.deployment?.compute?.size,
);
const monthlyBillingPeriod = computed(() => 'monthly');
const annualBillingPeriod = computed(() => 'annual');
const { wnextAppPrice: monthlyPrice } = usePrice(
  manifest,
  computeSize,
  salesTier,
  monthlyBillingPeriod,
);
const { wnextAppPrice: annualPrice } = usePrice(
  manifest,
  computeSize,
  salesTier,
  annualBillingPeriod,
);
const formattedMonthlyPrice = computed(() => formatWNextPrice(monthlyPrice.value));
const formattedAnnualPrice = computed(() => formatWNextPrice(annualPrice.value));
</script>

<template>
  <div class="billing-period">
    <AppLauncherHero
      :app-logo="metadata"
      :app-title="metadata.name"
      :right-image-src="FoxAndClover"
      heading-text="Choose Billing"
    />

    <div class="section-highlighted background-gradient--base billing-period__card">
      <div class="billing-period__eyebrow">{{ salesTierLabel }}</div>
      <div class="billing-period__title">
        Select how this Clovyr AI Cloud subscription should bill.
      </div>
      <div class="billing-period__options">
        <button
          class="billing-period__option"
          :class="{ 'billing-period__option--selected': billingPeriod === 'monthly' }"
          type="button"
          @click="setBillingPeriod('monthly')"
        >
          <span>Monthly</span>
          <strong>${{ formattedMonthlyPrice }}/mo</strong>
        </button>
        <button
          class="billing-period__option"
          :class="{ 'billing-period__option--selected': billingPeriod === 'annual' }"
          type="button"
          @click="setBillingPeriod('annual')"
        >
          <span>Annual</span>
          <strong>${{ formattedAnnualPrice }}/yr</strong>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';

.billing-period__card {
  padding: grid-vars.space(4);
}

.billing-period__eyebrow {
  color: colors.color(grey, tertiary);
  margin-bottom: 0.75rem;
}

.billing-period__title {
  color: colors.color(white);
  font-size: 1.125rem;
  margin-bottom: 1.5rem;
}

.billing-period__options {
  display: grid;
  gap: 1rem;
}

.billing-period__option {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  color: colors.color(white);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  text-align: left;
}

.billing-period__option--selected {
  border-color: #33e672;
  box-shadow: 0 0 0 1px #33e672 inset;
}
</style>
